import * as React from "react";
import { KeyValue, Query } from "../../../interfaces";
import SODCountyList from "./SODCountyList";
import { TableRow, TableCell, Button, Chip, Checkbox } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import generateBranchOfficeDepositsApiUrl from "./BranchOfficeDepositsUtilities";
import { fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import useOpenCloseAll from "../../../hooks/useOpenCloseAll";
import LoadingIndicator from "../../LoadingIndicator";
import { selectedStatesAlpha } from "../../../refdata/FilterOptions";
import {
  chunkArray,
  reduceObj,
  sortObjValue,
} from "../../../common/services/Utilities";

interface Props {
  appState: Query;
  openClose: string;
  setOpenClose: (openClose: string) => void;
  openCloseAllDisplay: KeyValue;
  setOpenCloseAllDisplay: (openCloseAllDisplay: KeyValue) => void;
  checked?: KeyValue;
  setChecked?: (val: KeyValue) => void;
  parentName?: string;
  cert?: string;
  fromBHC?: boolean;
  locationOnlySearch?: boolean;
}

export default function SODStateList({
  appState,
  openClose,
  parentName,
  openCloseAllDisplay,
  setOpenCloseAllDisplay,
  setOpenClose,
  cert,
  fromBHC,
  locationOnlySearch,
  checked,
  setChecked,
}: Props) {
  const [open, setOpen] = React.useState<string[]>(
    appState.filters.stname ? [appState.filters.stname] : [],
  );
  const [stateData, setStateData] = React.useState<KeyValue[]>([]);
  const [stateTotals, setStateTotals] = React.useState<KeyValue>({
    count: 0,
    sum_DEPSUMBR: 0,
  });
  const [openCloseState, setOpenCloseState] = React.useState("");
  const [previousSearch, setPreviousSearch] = React.useState<KeyValue>({});
  const [openAllButton, setOpenAllButton] = React.useState<KeyValue>({});
  const [loading, setLoading] = React.useState(false);
  const [allStatesLocationOnlySearch, setAllStatesLocationOnlySearch] =
    React.useState(false);
  const [totalInstitutions, setTotalInstitutions] = React.useState(0);
  const isBHC = appState.ui.institutionType === "bhcs";
  const key = isBHC ? "RSSDHCR" : "CERT";
  const isCountyView = Boolean(appState.filters.stalpbr !== "");

  let bhcFilter = "";
  if (locationOnlySearch && isBHC) {
    bhcFilter = "RSSDHCR:>0";
  }

  useOpenCloseAll({
    data: stateData,
    key: "STALPBR",
    currentValues: open,
    openClose: openClose === parentName || openClose === "ALL",
    parent: parentName || "ALL",
    setValues: setOpen,
    setParent: setOpenClose,
    buttonDisplays: openCloseAllDisplay,
    setButtonDisplays: setOpenCloseAllDisplay,
  });

  const [initialLoad, setInitialLoad] = React.useState(true);
  React.useEffect(() => {
    if (
      checked &&
      open.length > 0 &&
      open.every((key) => checked[key] && checked[key].count > 0)
    ) {
      open.forEach((key) => {
        checked[key].count = 0;
        checked[key].deposits = 0;
      });
    }
  }, [checked, open]);

  React.useEffect(() => {
    if (
      initialLoad &&
      checked &&
      stateData.length > 0 &&
      appState.filters.cert &&
      checked &&
      appState.filters.lastYear &&
      setChecked
    ) {
      setInitialLoad(false);
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=YEAR:${
            appState.filters.lastYear
          } AND BKMO:1 AND CERT:${
            appState.filters.cert
          }&limit=1&fields=RSSDHCR,STNAMEBR`,
        )
          .then((response) => {
            if (isMounted) {
              const newStateData = stateData.map((data) => {
                return {
                  ...data,
                  RSSDHCR: response.data[0].RSSDHCR,
                  STNAMEBR: response.data[0].STNAMEBR,
                };
              });
              const allStatesChecked = reduceObj(
                newStateData.map((state: KeyValue) => {
                  return {
                    key: state.STALPBR,
                    value: {
                      checked:
                        Object.keys(checked).length === 0
                          ? appState.ui.partialMerger?.[0]?.isChecked
                          : checked[state.STALPBR]?.checked,
                      count: state.count,
                      deposits: state.sum_DEPSUMBR,
                      cert: appState.filters.cert || cert,
                      rssdhcr: state?.RSSDHCR,
                    },
                  };
                }),
              );
              allStatesChecked &&
                setChecked((prev: KeyValue) => ({
                  ...prev,
                  ...allStatesChecked,
                }));
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [
    setChecked,
    appState.filters.cert,
    appState.filters.lastYear,
    initialLoad,
    checked,
    cert,
    appState.ui.partialMerger,
    stateData,
  ]);

  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      marketSelect: appState.ui.marketSelection,
    };
    if (
      ((appState.ui.marketSelection &&
        Object.keys(appState.ui.marketSelection[0]).length > 0) ||
        appState.filters.cert ||
        cert ||
        appState.filters.OTSDocket ||
        appState.filters.rssdid) &&
      Object.keys(appState.ui.marketSelection[0]).length > 0 &&
      appState.filters.stateNames[0] !== "ALL" &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      setOpen([]);
      setAllStatesLocationOnlySearch(false);
      setLoading(true);

      const fetchUrls = appState.filters.stateNames
        .filter((state: string) => state)
        .map((state: string, index: number) => {
          const marketSelection = appState.ui.marketSelection[index];
          const zips = chunkArray(marketSelection?.zips, 500);
          return zips.map((zipChunk) =>
            generateBranchOfficeDepositsApiUrl(
              cert
                ? { ...appState, filters: { ...appState.filters, cert } }
                : appState,
              {
                by: "STALPBR",
                limit: 100,
                sum: ["DEPSUMBR"],
              },
              state,
              { ...marketSelection, zips: zipChunk },
              key,
              bhcFilter,
            ),
          );
        });
      setPreviousSearch(filtersSearch);
      let isMounted = true;

      (async () => {
        await Promise.all(
          fetchUrls
            .flat()
            .map((url: string) => fetchData(`${ApiBaseUrl()}/sod`, url)),
        )
          .then((response) => {
            if (isMounted) {
              const filteredData = response
                .map((res: KeyValue) => res.data[0])
                .filter((resData) => resData)
                .reduce((acc, obj) => {
                  const state = obj.STALPBR;
                  if (!acc[state]) {
                    acc[state] = {
                      STALPBR: state,
                      count: 0,
                      sum_DEPSUMBR: 0,
                      unique_count: 0,
                    };
                  }
                  acc[state].count += obj.count;
                  acc[state].sum_DEPSUMBR += obj.sum_DEPSUMBR;
                  acc[state].unique_count += obj.unique_count;
                  return acc;
                }, {});
              const data: KeyValue[] = Object.values(filteredData);

              const arrayOfUniqueInstitutionsTotal = response.map(
                (res: KeyValue) => {
                  return res.totals.count;
                },
              );

              const arrayOfBranchOfficesTotal = response.map(
                (res: KeyValue) => {
                  return res.data[0] ? res.data[0].count : 0;
                },
              );

              const arrayOfDepSum = response.map((res: KeyValue) => {
                return res.totals.sum_DEPSUMBR ? res.totals.sum_DEPSUMBR : 0;
              });
              setStateData(data);
              if (isCountyView) {
                setOpen(data.map((d: KeyValue) => d.STALPBR));
              }
              setStateTotals({
                count: arrayOfUniqueInstitutionsTotal.reduce(
                  (a, b) => a + b,
                  0,
                ),
                unique_count: arrayOfBranchOfficesTotal.reduce(
                  (a, b) => a + b,
                  0,
                ),
                sum_DEPSUMBR: arrayOfDepSum.reduce((a, b) => a + b, 0),
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            setLoading(false);
            isMounted = false;
          });
      })();
    }
  }, [
    appState,
    cert,
    previousSearch,
    locationOnlySearch,
    key,
    bhcFilter,
    isCountyView,
  ]);
  React.useEffect(() => {
    const filtersSearch = {
      filters: appState.filters,
      search: appState.search,
      sort: appState.sort,
      ui: appState.ui.institutionTypes,
    };
    if (
      (appState.filters.msa ||
        appState.filters.zipbr ||
        appState.filters.stateNames[0] ||
        appState.filters.cert ||
        cert ||
        appState.filters.OTSDocket ||
        appState.filters.rssdid) &&
      (Object.keys(appState.ui.marketSelection[0]).length === 0 ||
        appState.filters.stateNames[0] === "ALL") &&
      JSON.stringify(filtersSearch) !== JSON.stringify(previousSearch)
    ) {
      if (appState.filters.stateNames[0] === "ALL") {
        setAllStatesLocationOnlySearch(true);
      } else {
        setAllStatesLocationOnlySearch(false);
      }
      setOpen([]);
      setLoading(true);
      setPreviousSearch(filtersSearch);
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod`,
          generateBranchOfficeDepositsApiUrl(
            cert
              ? {
                  ...appState,
                  filters: {
                    ...appState.filters,
                    cert,
                  },
                }
              : appState,
            {
              by: "STALPBR",
              limit: 1000,
              sum: ["DEPSUMBR"],
            },
            undefined,
            undefined,
            locationOnlySearch ? key : undefined,
            bhcFilter,
          ),
        )
          .then((response) => {
            if (isMounted) {
              setOpen([appState.filters.stname]);
              setStateData(response.data);
              if (isCountyView) {
                setOpen(response.data.map((d: KeyValue) => d.STALPBR));
              }
              sortObjValue(response.data, "STNAMEBR");
              const arrayOfBranchOfficesTotal = response.data.map(
                (data: any) => data.count,
              );
              const branchCount = arrayOfBranchOfficesTotal.reduce(
                (a: any, b: any) => a + b,
                0,
              );

              setStateTotals({ ...response.totals, unique_count: branchCount });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => {
            isMounted = false;
            setLoading(false);
          });
      })();
    }
  }, [
    appState,
    bhcFilter,
    cert,
    key,
    locationOnlySearch,
    previousSearch,
    isCountyView,
  ]);

  React.useEffect(() => {
    // get total count of institutions
    if (
      Object.keys(totalInstitutions).length === 0 &&
      appState.filters.lastYear &&
      appState.filters.stateNames[0] === "ALL" &&
      locationOnlySearch
    ) {
      let isMounted = true;
      const bhcFilter = isBHC ? " AND RSSDHCR:>0" : "";
      let institutions = 0;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?&agg_by=CERT&filters=YEAR:${
            appState.filters.lastYear
          } AND BKMO:1${bhcFilter}&limit=1`,
        )
          .then((response) => {
            if (isMounted) {
              setAllStatesLocationOnlySearch(true);
              institutions = response.totals.count;
              setTotalInstitutions(institutions);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          })
          .finally(() => (isMounted = false));
      })();
    }
  }, [
    totalInstitutions,
    appState.filters.lastYear,
    appState.filters.stateNames,
    locationOnlySearch,
    isBHC,
  ]);

  const stateWithName: KeyValue[] = stateData.map((data) => {
    const stateName = selectedStatesAlpha.find(
      (state: KeyValue) => state.value === data.STALPBR,
    )?.label;
    return { ...data, STNAME: stateName };
  });
  const sortedStateRows = sortObjValue(stateWithName, "STNAME");

  const stateRows = sortedStateRows.map((data, int) => {
    const expandCollapse = (e: any) => {
      const otherClicks = ["Open All", "Close All"];
      if (!otherClicks.includes(e.target.innerText)) {
        const key = parentName || "ALL";
        setOpenCloseAllDisplay({
          ...openCloseAllDisplay,
          [key]: {
            ...openCloseAllDisplay[key],
            [data.STALPBR]: !open.includes(data.STALPBR),
          },
        });
        open.includes(data.STALPBR) && setOpenAllButton({});
        !open.includes(data.STALPBR)
          ? setOpen([...open, data.STALPBR])
          : setOpen(open.filter((state) => state !== data.STALPBR));
        if (checked && setChecked && checked[data.STALPBR]) {
          checked[data.STALPBR].count = 0;
          checked[data.STALPBR].deposits = 0;
        }
      }
    };
    const locationOnlyTableColumns = (data: KeyValue) => {
      return (
        <>
          <TableCell />
          <TableCell style={{ color: "#0000EE", textDecoration: "underline" }}>
            {data.unique_count ? data.unique_count.toLocaleString() : 0}
          </TableCell>
          <TableCell style={{ color: "#0000EE", textDecoration: "underline" }}>
            {data.count ? data.count.toLocaleString() : 0}
          </TableCell>
          <TableCell />
          {!isBHC && <TableCell />}
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR ? data.sum_DEPSUMBR.toLocaleString() : 0}
          </TableCell>
        </>
      );
    };
    const certTableColumns = (data: KeyValue) => {
      const subCheckBoxes =
        checked &&
        Object.keys(checked)
          .map((check) =>
            check.includes(`${data.STALPBR}_`) ? check : undefined,
          )
          .filter((data) => data);

      const indeterminateState =
        subCheckBoxes &&
        subCheckBoxes.some((subData: any) => checked[subData].checked) &&
        subCheckBoxes.some((subData: any) => !checked[subData].checked);
      return (
        <>
          {appState.ui.partialMerger?.length > 0 &&
            appState.filters.cert !== appState.ui.certAcquiring &&
            checked &&
            checked[data.STALPBR] &&
            setChecked && (
              <TableCell>
                <Checkbox
                  id={`${data.STALPBR}_checkbox`}
                  checked={
                    subCheckBoxes?.length
                      ? subCheckBoxes.every(
                          (subData: any) => checked[subData]?.checked,
                        )
                      : checked[data.STALPBR].checked
                  }
                  sx={{
                    "&:hover": {
                      backgroundColor: "#FFFFFF",
                    },
                  }}
                  inputProps={{ "aria-label": "checkbox" }}
                  onChange={() => {
                    const allStateChecks: any = Object.keys(checked)
                      .map((val) =>
                        val.startsWith(`${data.STALPBR}_`) ||
                        val === data.STALPBR
                          ? {
                              key: val,
                              value: indeterminateState
                                ? {
                                    ...checked[val],
                                    checked: false,
                                  }
                                : {
                                    ...checked[val],
                                    checked: !checked[data.STALPBR]?.checked,
                                  },
                            }
                          : undefined,
                      )
                      .filter((data) => data);
                    setChecked({
                      ...checked,
                      ...reduceObj(allStateChecks),
                    });
                  }}
                  indeterminate={indeterminateState}
                />
              </TableCell>
            )}
          <TableCell {...expandCollapseProps} />
          <TableCell {...expandCollapseProps} />
          <TableCell
            {...expandCollapseProps}
            style={{ color: "#0000EE", textDecoration: "underline" }}
          >
            {data.count ? data.count.toLocaleString() : 0}
          </TableCell>
          <TableCell {...expandCollapseProps} />
          <TableCell {...expandCollapseProps} />
          <TableCell {...expandCollapseProps} />
          <TableCell {...expandCollapseProps} />
          <TableCell {...expandCollapseProps} style={{ textAlign: "right" }}>
            {appState?.ui?.pgcStep === "step1"
              ? ""
              : data.sum_DEPSUMBR
                ? data.sum_DEPSUMBR.toLocaleString()
                : 0}
          </TableCell>
        </>
      );
    };
    const bhcsTableColumns = (data: KeyValue) => {
      return (
        <>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell>{data.count ? data.count.toLocaleString() : 0}</TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell />
          <TableCell style={{ textAlign: "right" }}>
            {data.sum_DEPSUMBR ? data.sum_DEPSUMBR.toLocaleString() : 0}
          </TableCell>
        </>
      );
    };

    const expandCollapseProps = {
      onKeyDown: (e: any) => {
        if (e.key === "Enter") {
          expandCollapse(e);
        }
      },
      onClick: (e: any) => expandCollapse(e),
    };

    return (
      data?.STALPBR && (
        <React.Fragment key={`${data.STALPBR}_${int}`}>
          <TableRow
            id={`${data.STALPBR.toLowerCase().replace(/ /g, "")}_expand_${int}`}
            sx={{
              "& td.MuiTableCell-root.MuiTableCell-body": {
                backgroundColor: "#acc7df",
                fontWeight: "bold",
              },
              "&:hover td.MuiTableCell-root.MuiTableCell-body, &:focus-visible td.MuiTableCell-root.MuiTableCell-body":
                {
                  cursor: "pointer",
                  backgroundColor: "#CDD6E3",
                },
            }}
            tabIndex={0}
          >
            <TableCell
              sx={
                fromBHC
                  ? {
                      "&.MuiTableCell-root.MuiTableCell-body": {
                        textAlign: "left",
                        paddingLeft: "30px",
                      },
                    }
                  : {
                      "&.MuiTableCell-root.MuiTableCell-body": {
                        textAlign: "left",
                      },
                    }
              }
              {...expandCollapseProps}
            >
              {open.includes(data.STALPBR) ? (
                <Chip
                  sx={(theme) => ({
                    "cursor": "pointer",
                    "backgroundColor": "transparent",
                    "color": "#0000EE",
                    "textDecoration": "underline",
                    "fontSize": 14,
                    "float": "left",
                    "& .MuiChip-icon": {
                      color: theme.palette.primary.main,
                    },
                  })}
                  icon={<ExpandMoreIcon />}
                  label={`${
                    selectedStatesAlpha.find(
                      (state: KeyValue) => state.value === data.STALPBR,
                    )?.label ?? ""
                  }`}
                />
              ) : (
                <Chip
                  sx={(theme) => ({
                    "cursor": "pointer",
                    "backgroundColor": "transparent",
                    "color": "#0000EE",
                    "textDecoration": "underline",
                    "fontSize": 14,
                    "float": "left",
                    "& .MuiChip-icon": {
                      color: theme.palette.primary.main,
                    },
                  })}
                  icon={<ChevronRightIcon />}
                  label={data.STNAME}
                />
              )}
            </TableCell>
            <TableCell style={{ padding: 0 }} {...expandCollapseProps}>
              {openAllButton[data.STALPBR] &&
                Object.keys(openAllButton[data.STALPBR]).length > 1 &&
                open.includes(data.STALPBR) && (
                  <Button
                    id={
                      Object.values(openAllButton[data.STALPBR]).every(
                        (val) => val,
                      )
                        ? `closeAllCounties_${data.STALPBR}`
                        : `openAllCounties_${data.STALPBR}`
                    }
                    sx={(theme) => ({
                      "minWidth": 80,
                      "margin": 0,
                      "color": theme.palette.primary.main,
                      "fontWeight": 600,
                      "&:hover, &.Mui-focusVisible": {
                        backgroundColor: "#fff",
                      },
                    })}
                    onClick={() => {
                      setOpenCloseState(data.STALPBR);
                    }}
                  >
                    {Object.values(openAllButton[data.STALPBR]).every(
                      (val) => val,
                    )
                      ? "Close All"
                      : "Open All"}
                  </Button>
                )}
            </TableCell>

            {locationOnlySearch
              ? locationOnlyTableColumns(data)
              : !fromBHC
                ? certTableColumns(data)
                : bhcsTableColumns(data)}
          </TableRow>
          {open.includes(data.STALPBR) && (
            <SODCountyList
              stateIndex={int}
              stateName={data.STALPBR}
              appState={appState}
              setOpenClose={setOpenCloseState}
              openClose={openCloseState}
              fromBHC={fromBHC}
              cert={cert}
              openCloseAllDisplay={openAllButton}
              setOpenCloseAllDisplay={setOpenAllButton}
              locationOnlySearch={locationOnlySearch}
              checked={checked}
              setChecked={setChecked}
            />
          )}
        </React.Fragment>
      )
    );
  });
  return (
    <>
      {stateTotals.count > 0 || cert ? (
        <>
          {stateRows}
          {locationOnlySearch ? (
            <TableRow
              sx={{
                "& td.MuiTableCell-root.MuiTableCell-body": {
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: "#ffffff",
                  fontWeight: "bold",
                },
              }}
            >
              <TableCell style={{ textAlign: "left", paddingLeft: "1rem" }}>
                Totals
              </TableCell>
              {appState.ui.partialMerger?.length > 0 &&
                appState.filters.cert !== appState.ui.certAcquiring && (
                  <TableCell />
                )}
              <TableCell />
              <TableCell />
              <TableCell>
                {allStatesLocationOnlySearch
                  ? totalInstitutions
                    ? totalInstitutions.toLocaleString()
                    : 0
                  : stateTotals?.count
                    ? stateTotals.count.toLocaleString()
                    : 0}
              </TableCell>
              <TableCell>
                {stateTotals.unique_count
                  ? stateTotals.unique_count.toLocaleString()
                  : 0}
              </TableCell>
              <TableCell />
              {!isBHC && <TableCell />}
              <TableCell style={{ textAlign: "right" }}>
                {stateTotals.sum_DEPSUMBR
                  ? stateTotals.sum_DEPSUMBR.toLocaleString()
                  : 0}
              </TableCell>
            </TableRow>
          ) : (
            !fromBHC && (
              <TableRow
                sx={{
                  "& td.MuiTableCell-root.MuiTableCell-body": {
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "#ffffff",
                    fontWeight: "bold",
                  },
                }}
              >
                <TableCell style={{ textAlign: "left", paddingLeft: "1rem" }}>
                  Totals
                </TableCell>
                <TableCell />
                {appState.ui.partialMerger?.length > 0 &&
                  appState.filters.cert !== appState.ui.certAcquiring && (
                    <TableCell />
                  )}
                <TableCell />
                <TableCell />
                <TableCell>
                  {stateTotals.unique_count
                    ? stateTotals.unique_count.toLocaleString()
                    : 0}
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell />
                <TableCell style={{ textAlign: "right" }}>
                  {stateTotals.sum_DEPSUMBR
                    ? stateTotals.sum_DEPSUMBR.toLocaleString()
                    : 0}
                </TableCell>
              </TableRow>
            )
          )}
        </>
      ) : (
        <TableRow
          sx={{
            "&.MuiTableRow-root td": {
              height: !isCountyView ? 300 : 0,
              paddingTop: "1rem",
              fontSize: "1rem",
              display: "block",
              position: "absolute",
              backgroundColor: "#fff",
            },
          }}
        >
          <TableCell>
            {loading ? (
              <LoadingIndicator
                text="Loading"
                spinnerSize={32}
                textSx={{
                  fontSize: 20,
                  marginLeft: 150,
                }}
                spinnerSx={{
                  marginTop: 24,
                  marginLeft: 150,
                }}
              />
            ) : (
              "Double-check your search criteria for errors, or try your search again limiting or removing location-based criteria."
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
